import React from 'react';
import { motion } from 'motion/react';
import { getSignedMediaUrl } from '@/lib/utils';

interface BookingCategoryImageCardProps {
  /** Image ID of the booking category card */
  imageId?: string | null;
  /** Name of the booking category (ticket/table type) */
  categoryName?: string;
  /** Click handler for the card */
  onClick?: () => void;
  /** Optional width class (default: w-48) */
  widthClass?: string;
  /** Whether to show the hyperlink icon badge */
  showLinkBadge?: boolean;
}

/**
 * Reusable booking category image card with gradient overlay.
 * Used for displaying ticket/table types in cart and transfer pages.
 */
export const BookingCategoryImageCard: React.FC<BookingCategoryImageCardProps> = ({
  imageId,
  categoryName = 'General Admission',
  onClick,
  widthClass = 'w-48',
  showLinkBadge = true,
}) => {
  // Check if image is valid
  const hasValidImage = imageId && imageId !== '00000000-0000-0000-0000-000000000000';
  const imageUrl = hasValidImage ? getSignedMediaUrl(imageId, 'image').url : null;

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`relative aspect-[4/3] ${widthClass} rounded-lg overflow-hidden cursor-pointer group flex-shrink-0 hover:ring-2 hover:ring-blue-500/50`}
      style={{
        background: imageUrl
          ? `url(${imageUrl})`
          : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

      {/* Hyperlink Icon Badge - Top Right */}
      {showLinkBadge && (
        <div className="absolute top-2 right-2 z-10 bg-black/60 rounded-full p-1.5 group-hover:bg-black/80 transition-colors">
          <svg
            className="w-4 h-4 text-blue-400 group-hover:text-blue-300 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </div>
      )}

      {/* Category Name Overlay - Bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-2.5 z-10">
        <p className="text-white font-semibold text-base line-clamp-1">{categoryName}</p>
      </div>

      {/* Hover Effect */}
      <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/10 transition-colors" />
    </motion.div>
  );
};

export default BookingCategoryImageCard;
